import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Checkbox,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import { Icon } from '@ellucian/ds-icons/lib';

interface SelfEntitleConfirmModalProps {
  open: boolean;
  solutionName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const SelfEntitleConfirmModal: React.FC<SelfEntitleConfirmModalProps> = ({
  open,
  solutionName,
  onConfirm,
  onCancel,
  isSubmitting = false,
}) => {
  const [understood, setUnderstood] = useState(false);

  const handleClose = () => {
    setUnderstood(false);
    onCancel();
  };

  const handleConfirm = () => {
    // Don't reset understood here - keep checkbox checked during submission
    // Only reset when modal closes/cancels
    onConfirm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          display: 'flex',
          width: { xs: 'calc(100% - 32px)', sm: '700px' },
          maxWidth: { xs: 'calc(100% - 32px)', sm: '700px' },
          maxHeight: '90vh',
          overflowY: 'auto',
          borderRadius: '0px',
          boxShadow: '0px 8px 32px 0px rgba(0,0,0,0.16)',
          flexDirection: 'column',
          background: '#fff',
          p: { xs: '8px', sm: '34px' },
          margin: { xs: '16px', sm: 'auto' },
          position: 'relative',
        }
      }}
    >
      <IconButton
        aria-label="Close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: { xs: 12, sm: 20 },
          top: { xs: 12, sm: 20 },
          zIndex: 2,
          color: '#6B7280',
        }}
      >
        <Icon name="close" style={{ width: 20, height: 20 }} />
      </IconButton>
      <DialogTitle
        sx={{
          fontSize: '27px',
          fontWeight: 600,
          fontFamily: '"Poppins", "Proxima Nova", Montserrat, Aptos, Roboto',
          paddingBottom: '16px',
          borderBottom: '1px solid #e0e0e0',
          m: 0,
          p: 0,
          marginBottom: '26px',
        }}
      >
        Confirm Integration Selection?
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: '"Poppins", "Proxima Nova", Montserrat, Aptos, Roboto', }}>
          <Typography sx={{ fontSize: '16.5px', fontWeight: 500, lineHeight: 1.5 }}>
            You're about to select this integration as part of your institution's core
            integrations.
          </Typography>

          <Typography sx={{ fontSize: '16.5px', fontWeight: 600, lineHeight: 1.5 }}>
            Once confirmed, these selections cannot be changed.
          </Typography>

          <Typography sx={{ fontSize: '15px', lineHeight: 1.5, fontFamily: '"Poppins", "Proxima Nova", Montserrat, Aptos, Roboto', }}>
            After confirmation, you'll be able to install these integrations and choose
            environments when you're ready.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              marginTop: '16px',
            }}
          >
            <Checkbox
              checked={understood}
              onChange={(e) => setUnderstood(e.target.checked)}
              inputProps={{ 'aria-label': 'I understand this selection cannot be changed once confirmed' }}
              sx={{
                marginTop: '2px',
                padding: '0px',
                '&.Mui-checked': {
                  color: '#026BC8',
                },
              }}
            />
            <Typography sx={{ fontSize: '15px', lineHeight: 1.6, fontFamily: '"Poppins", "Proxima Nova", Montserrat, Aptos, Roboto', }}>
              I understand this selection cannot be changed once confirmed.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 0,
          pt: 2,
          gap: '12px',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'flex-end',
          paddingTop: '14px',
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 600,
            color: '#026BC8',
            border: '2px solid #026BC8',
            padding: '9px 24px',
            fontFamily: '"Poppins", "Proxima Nova", Montserrat, Aptos, Roboto',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#f5f5f5',
              border: '2px solid #026BC8',
            },
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleConfirm}
          disabled={!understood || isSubmitting}
          sx={{
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 600,
            color: '#ffffff',
            fontFamily: '"Poppins", "Proxima Nova", Montserrat, Aptos, Roboto',
            backgroundColor: (understood && !isSubmitting) ? '#026BC8' : '#cccccc',
            padding: '9px 24px',
            borderRadius: '4px',
            border: 'none',
            cursor: (understood && !isSubmitting) ? 'pointer' : 'not-allowed',
            '&:hover': {
              backgroundColor: (understood && !isSubmitting) ? '#0055A4' : '#cccccc',
            },
            '&:disabled': {
              backgroundColor: '#cccccc',
              cursor: 'not-allowed',
            },
          }}
        >
          {isSubmitting ? 'Confirming...' : 'Confirm Selection'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelfEntitleConfirmModal;
